import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EntityCrudActions } from 'actions';
import { KPIS } from 'constants/Entities';
import Bool from 'containers/components/Fields/Bool';
import Text from 'containers/components/Fields/Text';
import { getActiveCrudName } from 'utils/crud';
import { getLiteral } from 'utils/getLiteral';

import GoalFieldAggregatedTable from './components/GoalFieldAggregatedTable';
import PeriodsField from './components/Periods';
import UsersField from './components/Users';
import { resetGoalError, resetPeriodErrors, resetUsersErrors } from './utils';

import './styles.scss';

const GoalField = memo(({ changeField, data, errors, field, firstErrorField, getError }) => {
    const dispatch = useDispatch();
    const dataRef = useRef(data);
    const fieldRef = useRef(null);

    // ========================================
    // REDUX STATE & SELECTORS
    // ========================================
    const mappedSchema = useSelector((state) => {
        const active = getActiveCrudName(state);
        const entityCrud = active && state.entityCrud[active];
        return entityCrud?.mappedSchema;
    });

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const {
        goalFieldLabel,
        goalToggleLabel,
        isIndividualSelected,
        isTeamAggregatedSelected,
        isTeamSelected,
        popoverText,
    } = useMemo(() => {
        const isIndividualSelected = data.assignment === 'user';
        const isTeamSelected = data.assignment === 'team';
        const isTeamAggregatedSelected = data.assignment === 'teamaggregated';
        const goalFieldLabel =
            data.assignment === 'user' || !data.assignment
                ? getLiteral('label_inidividual_goal_to_reach')
                : getLiteral('label_team_goal_to_reach');

        let goalToggleLabel = '';
        let popoverText = '';
        switch (data.period) {
            case 'month':
                goalToggleLabel = getLiteral('label_goal_for_each_month');
                popoverText = getLiteral('helptext_goal_for_each_month');
                break;
            case 'quarter':
                goalToggleLabel = getLiteral('label_goal_for_each_quarter');
                popoverText = getLiteral('helptext_goal_for_each_quarter');
                break;
            case 'fourMonthly':
                goalToggleLabel = getLiteral('label_goal_for_each_third');
                popoverText = getLiteral('helptext_goal_for_each_third');
                break;
            case 'semester':
                goalToggleLabel = getLiteral('label_goal_for_each_semester');
                popoverText = getLiteral('helptext_goal_for_each_semester');
                break;
        }

        return {
            goalFieldLabel,
            goalToggleLabel,
            isIndividualSelected,
            isTeamAggregatedSelected,
            isTeamSelected,
            popoverText,
        };
    }, [data.assignment, data.period]);

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const onChangeToggle = useCallback(
        (fieldId) => {
            return (value) => {
                if (!value) {
                    if (isIndividualSelected || isTeamSelected) {
                        const { newErrors, newFirstErrorField } = resetPeriodErrors(
                            mappedSchema,
                            firstErrorField,
                            errors,
                        );
                        dispatch(
                            EntityCrudActions.forceUpdateErrors(KPIS, {
                                errors: newErrors,
                                firstErrorField: newFirstErrorField,
                            }),
                        );
                    } else if (isTeamAggregatedSelected) {
                        const { newErrors, newFirstErrorField } = resetUsersErrors(
                            mappedSchema,
                            firstErrorField,
                            errors,
                        );
                        dispatch(
                            EntityCrudActions.forceUpdateErrors(KPIS, {
                                errors: newErrors,
                                firstErrorField: newFirstErrorField,
                            }),
                        );
                    }
                } else if (value) {
                    const { newErrors, newFirstErrorField } = resetGoalError(
                        firstErrorField,
                        errors,
                    );
                    dispatch(
                        EntityCrudActions.forceUpdateErrors(KPIS, {
                            errors: newErrors,
                            firstErrorField: newFirstErrorField,
                        }),
                    );
                }
                changeField(fieldId)(value);
            };
        },
        [
            changeField,
            dispatch,
            errors,
            firstErrorField,
            isIndividualSelected,
            isTeamAggregatedSelected,
            isTeamSelected,
            mappedSchema,
        ],
    );

    const renderFields = useMemo(() => {
        return field.inputs.map((current, index) => {
            if (current.id === 'goal') {
                return (
                    <Text
                        key={index}
                        error={getError(errors[current.id], [current.dataLength])}
                        hint={current.placeholder}
                        isSafeNumber={current.isSafeNumber}
                        label={goalFieldLabel}
                        labelMode="horizontal"
                        mandatory={current.mandatory}
                        onChange={changeField(current.id)}
                        readOnly={data.goalToggle}
                        type="number"
                        value={data[current.id] || ''}
                    />
                );
            } else if (
                current.id === 'goalToggle' &&
                (isIndividualSelected || isTeamSelected || isTeamAggregatedSelected) &&
                goalToggleLabel
            ) {
                return (
                    <div key={index} className="goal-field-container__goal-toggle-field">
                        <Bool
                            description={popoverText}
                            label={goalToggleLabel}
                            onChange={onChangeToggle(current.id)}
                            value={data.goalToggle}
                        />
                    </div>
                );
            } else if (current.id === 'valuesDefined') {
                if (
                    (isIndividualSelected || isTeamSelected) &&
                    goalToggleLabel &&
                    data.goalToggle
                ) {
                    return (
                        <PeriodsField
                            key={index}
                            data={data}
                            errors={errors}
                            field={current}
                            getError={getError}
                            onChange={changeField(current.id)}
                            value={data[current.id]}
                        />
                    );
                } else if (isTeamAggregatedSelected) {
                    if (!data.goalToggle) {
                        return (
                            <UsersField
                                key={index}
                                data={data}
                                errors={errors}
                                field={current}
                                getError={getError}
                                onChange={changeField(current.id)}
                                value={data[current.id]}
                            />
                        );
                    } else {
                        const fieldsTable = current.fieldsTable || null;
                        return (
                            <GoalFieldAggregatedTable
                                key={index}
                                data={data}
                                errors={errors['aggregatedMatrix']}
                                onChange={changeField(current.id)}
                                periods={fieldsTable.fieldsPeriods}
                                users={fieldsTable.fieldsUsers}
                            />
                        );
                    }
                }
            }
            return null;
        });
    }, [
        changeField,
        data,
        errors,
        getError,
        goalFieldLabel,
        goalToggleLabel,
        field.inputs,
        isIndividualSelected,
        isTeamAggregatedSelected,
        isTeamSelected,
        onChangeToggle,
        popoverText,
    ]);

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        if (
            firstErrorField &&
            ['goal', 'goaltoggle', 'valuesdefined'].includes(firstErrorField) &&
            fieldRef.current
        ) {
            fieldRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [firstErrorField]);

    useEffect(() => {
        if (
            dataRef.current.period !== data.period &&
            (data.period !== 'month' ||
                data.period !== 'quarter' ||
                data.period !== 'fourMonthly' ||
                data.period !== 'semester')
        ) {
            dataRef.current.period = data.period;
            changeField('goalToggle')(false);
        } else dataRef.current.period = data.period;
    }, [data.period, changeField, data]);

    // ========================================
    // RENDER
    // ========================================
    let classes = ['goal-field-container'];
    if (isTeamAggregatedSelected) classes.push('goal-field-container-wide');

    return (
        <div ref={fieldRef} className={classes.join(' ')}>
            <div className="goal-field-content">{renderFields}</div>
        </div>
    );
});

GoalField.displayName = 'GoalField';

export default GoalField;
